<template>
  <div
    v-if="Object.keys(booking_data).length && held_booking.id"
    class="ancillaries-page"
  >
    <b-container class="my-4" :class="{ 'is-committing': commit_loading }">
      <b-row>
        <b-col class="ml-3">
          <button-back :forwards_only="forwards_only" />
        </b-col>
      </b-row>
    </b-container>

    <b-container>
      <section v-if="loading" class="section section--loader">
        <loader size="large" :show_timing_slot="true" />
      </section>

      <b-row v-else>
        <b-col
          sm="12"
          md="12"
          lg="8"
          xl="9"
          :class="{ 'is-committing': commit_loading }"
        >
          <steps-bar
            v-if="steps.length"
            :steps="steps"
            :active_id="active_step_id"
            :enabled_ids="visited_step_ids"
            :completed_ids="completed_step_ids"
            @select="set_active_step"
          />
          <div class="ancillaries-page__body">
            <div v-if="!steps.length">
              Nessun servizio aggiuntivo disponibile per questa prenotazione.
            </div>
            <template v-else>
              <additional-seats
                v-if="active_step_id === 'seat'"
                ref="additional_seats"
                :flight_offer="flight_offer"
                :seatmap="ancillaries.seat || {}"
                :travelers="travelers"
                :value="selections.seat || {}"
                @input="set_step_selections('seat', $event)"
                @seats_complete="seats_complete = $event"
                @status="seat_status = $event"
              />
              <additional-ancillaries
                v-else-if="active_step"
                :key="active_step_id"
                :step_id="active_step_id"
                :step_name="active_step.name"
                :flight_offer="flight_offer"
                :catalog="active_catalog"
                :travelers="travelers"
                :value="active_selections"
                :title="active_step.title"
                :description="active_step.description"
                @input="set_step_selections(active_step_id, $event)"
              />
            </template>
          </div>
        </b-col>

        <b-col sm="12" md="12" lg="4" xl="3">
          <div class="sidebar sticky-top">
            <div class="detail--panel detail--panel-sidebar pt-0" compact>
              <!-- Resume blurs during commit; the advance button is a
                   sibling so the filter's stacking context keeps it
                   crisp. -->
              <div :class="{ 'is-committing': commit_loading }">
                <sidebar-resume
                  :flight_offers="[base_flight_offer]"
                  :uncommitted_additions="uncommitted_additions"
                />
              </div>
              <btn-checkout
                v-if="steps.length"
                id="btn-checkout-ancillaries"
                :copy="advance_label"
                :conditions="advance_disabled"
                :popover="advance_popover"
                :loading="commit_loading"
                @handle_data="handle_advance()"
              />
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import {
    fetch_ancillaries,
    book_ancillaries,
    retrieve,
    seats_are_mandatory,
    has_capability_for,
  } from '@liveflight/providers';
  import { get_availability, log } from '@/utilities/commons';
  import { apply_markup_to_number } from '@liveflight/utilities/price';
  import {
    align_committed_to_catalog,
    build_committed_selections,
    build_uncommitted_seat_entry,
    build_uncommitted_step_entry,
    compute_ancillary_diff,
    derive_travelers,
    diagnose_seatmap,
    filter_catalog_by_included,
    strip_offer_additional_services,
  } from '@liveflight/utilities/additional-services';
  import { seat_tracking_context } from '@liveflight/utilities/rybbit';
  import { STEPS_INFO } from '@liveflight/resources/ancillaries';
  import { number, sum, larger } from 'mathjs';
  import Loader from '@components/blocks/loader';
  import SidebarResume from '@liveflight/components/blocks/resume/resume';
  import BtnCheckout from '@liveflight/components/atoms/btn-checkout';
  import StepsBar from '@components/library/blocks/steps-bar.vue';
  import AdditionalAncillaries from '@liveflight/components/blocks/ancillaries/additional-ancillaries.vue';
  import AdditionalSeats from '@liveflight/components/blocks/ancillaries/additional-seats.vue';

  export default {
    name: 'ancillaries-page',
    components: {
      loader: Loader,
      'sidebar-resume': SidebarResume,
      'btn-checkout': BtnCheckout,
      'steps-bar': StepsBar,
      'additional-ancillaries': AdditionalAncillaries,
      'additional-seats': AdditionalSeats,
    },
    data() {
      return {
        loading: true,
        commit_loading: false,
        ancillaries: {},
        selections: {},
        active_step_id: '',
        visited_step_ids: [],
        completed_step_ids: [],
        committed_at_retrieve: [],
        // Latest @seats_complete reading from <additional-seats>. The
        // seat step is conditionally rendered, so this holds its last
        // value while the user is on other steps. Starts closed; mounted
        // opens it when seats aren't mandatory, otherwise the child opens
        // it on first emit.
        seats_complete: false,
        // Latest @status reading from <seat-map>, relayed by
        // <additional-seats>: { isEditing, btnText, btnDisabled }. Empty
        // until the map mounts, which reads as "not editing" and leaves the
        // advance button in its plain checkout shape.
        seat_status: {},
      };
    },
    computed: {
      ...mapState(['booking_data']),
      ...mapState('liveflight', ['traveler_details']),
      flight_offer() {
        return this.$store.state.liveflight.flight_offer;
      },
      /**
       * Page-local view of the offer with booked-ancillary marks stripped
       * from price.additionalServices and fareDetailsBySegment[].additionalServices.
       * Drives the resume sidebar so committed ancillaries don't render
       * twice (offer + selections-derived uncommitted_additions). Vuex
       * `flight_offer` stays untouched for downstream pages.
       */
      base_flight_offer() {
        return strip_offer_additional_services(this.flight_offer);
      },
      held_booking() {
        return (
          this.$store.state.liveflight.held_booking || {
            id: '',
            expires_at: '',
          }
        );
      },
      steps() {
        return Object.keys(this.ancillaries || {})
          .filter(
            (key) =>
              STEPS_INFO[key] &&
              this.ancillaries[key] &&
              Object.keys(this.ancillaries[key]).length > 0 &&
              !this.ancillaries[key].empty
          )
          .sort((key1, key2) => STEPS_INFO[key1].order - STEPS_INFO[key2].order)
          .map((key) => ({ id: key, ...STEPS_INFO[key] }));
      },
      active_step() {
        return this.steps.find((s) => s.id === this.active_step_id) || null;
      },
      active_step_index() {
        return this.steps.findIndex((s) => s.id === this.active_step_id);
      },
      active_catalog() {
        return this.ancillaries[this.active_step_id] || {};
      },
      active_selections() {
        return this.selections[this.active_step_id] || {};
      },
      is_last_step() {
        return (
          this.steps.length > 0 &&
          this.active_step_index === this.steps.length - 1
        );
      },
      on_seat_step() {
        return this.active_step_id === 'seat';
      },
      /**
       * On the seat step the label is <seat-map>'s own per-segment cycle
       * ('Prossimo volo' / 'Avanti' on mobile, 'Conferma' on the last
       * segment); once the map collapses into its summary the button
       * commits and leaves for checkout.
       */
      advance_label() {
        if (this.on_seat_step) {
          if (this.seat_status.isEditing) return this.seat_status.btnText;
          return 'Vai al Checkout';
        }
        if (this.is_last_step) return 'Procedi al checkout';
        return 'Continua';
      },
      travelers() {
        return derive_travelers(this.flight_offer, this.traveler_details);
      },
      uncommitted_additions() {
        return [
          build_uncommitted_step_entry(
            this.selections.bag,
            'CHECKED_BAGS',
            this.ancillaries.bag
          ),
          build_uncommitted_step_entry(
            this.selections.sportsEquipment,
            'SPORTS_EQUIPMENT',
            this.ancillaries.sportsEquipment
          ),
          build_uncommitted_step_entry(
            this.selections.service,
            'SERVICES',
            this.ancillaries.service
          ),
          build_uncommitted_step_entry(
            this.selections.meal,
            'MEALS',
            this.ancillaries.meal
          ),
          build_uncommitted_step_entry(
            this.selections.loungeAccess,
            'LOUNGE_ACCESS',
            this.ancillaries.loungeAccess
          ),
          build_uncommitted_step_entry(
            this.selections.disabilityAssistance,
            'DISABILITY_ASSISTANCE',
            this.ancillaries.disabilityAssistance
          ),
          build_uncommitted_seat_entry(this.selections.seat),
        ].filter(Boolean);
      },
      uncommitted_additions_total() {
        return this.uncommitted_additions.reduce(
          (acc, entry) => sum(acc, number(Number(entry.amount) || 0)),
          number(0)
        );
      },
      projected_grand_total() {
        const { price = {} } = this.flight_offer || {};
        const base = Number(price.grandTotal) || Number(price.total) || 0;
        const raw = sum(base, this.uncommitted_additions_total);
        return Number(apply_markup_to_number(this.flight_offer, raw));
      },
      integration_credit() {
        const { details = {} } =
          get_availability(this.projected_grand_total) || {};
        return Number(details.integration) || 0;
      },
      credit_insufficient() {
        if (!this.integration_credit) return false;
        return larger(this.projected_grand_total, this.integration_credit);
      },
      /**
       * Block /ancillaries → /checkout when the fare requires explicit
       * seat picks and additional-seats still reports incomplete.
       * additional-seats owns the per-pax-per-segment walk; we combine
       * its boolean with the provider-level mandatory flag.
       */
      seat_gate_active() {
        return seats_are_mandatory(this.flight_offer) && !this.seats_complete;
      },
      forwards_only() {
        return has_capability_for(this.flight_offer, 'forwardsOnly');
      },
      /**
       * The seat step delegates its gate to <seat-map>: mandatory seats
       * block the advance until every pax is seated on the segment on
       * screen, and the last segment answers for the whole map — so the
       * coarser seat_gate_active would only re-lock segments the user has
       * already completed.
       */
      advance_disabled() {
        if (this.commit_loading || this.credit_insufficient) return true;
        if (this.on_seat_step) return !!this.seat_status.btnDisabled;
        return false;
      },
      advance_popover() {
        if (this.credit_insufficient) {
          return 'Credito insufficiente per coprire il totale con i servizi selezionati.';
        }
        if (this.seat_gate_active) {
          return 'Seleziona un posto per ogni passeggero su ogni volo prima di proseguire.';
        }
        return '';
      },
    },
    /**
     * Single guard + load. Splitting guard (created) from load (mounted)
     * was racy — `$router.replace` is async, so mounted fired the network
     * against an empty held_booking.id.
     */
    created() {
      if (!Object.keys(this.booking_data).length || !this.held_booking.id) {
        this.$router.replace('detail');
        return;
      }
      this.initialize_ancillaries({
        on_total_failure: () => this.$router.replace('checkout'), // On fail forget ancillaries and go to chekcout
      });
    },
    /**
     * Block navigation while book_ancillaries is in flight — browser back
     * during a commit would orphan the BE-side mutation.
     */
    beforeRouteLeave(to, from, next) {
      if (this.commit_loading) {
        next(false);
        return;
      }
      next();
    },
    methods: {
      /**
       * Retrieve confirms live state of the held booking (status + expiry);
       * ShopAncillaries returns the catalog. allSettled so a single
       * rejection doesn't drop the partial. Used on initial mount and
       * after a book_ancillaries failure to re-sync before retry.
       */
      initialize_ancillaries({ on_total_failure } = {}) {
        this.loading = true;
        return Promise.allSettled([
          retrieve(this.flight_offer, this.held_booking.id),
          fetch_ancillaries(this.flight_offer, this.held_booking.id),
        ])
          .then(([retrieve_settled, fetch_settled]) => {
            const retrieved =
              retrieve_settled.status === 'fulfilled'
                ? retrieve_settled.value
                : null;
            const envelope =
              fetch_settled.status === 'fulfilled' ? fetch_settled.value : null;
            if (!retrieved && !envelope) {
              window.eventBus.$emit('got_toast_notification', {
                text: 'Non riusciamo a caricare i servizi aggiuntivi.',
                variant: 'danger',
              });
              if (on_total_failure) on_total_failure();
              return;
            }
            if (retrieved && retrieved.flight_offer) {
              this.$store.commit(
                'liveflight/flight_offer',
                retrieved.flight_offer
              );
            }
            if (retrieved && retrieved.expiresAt) {
              this.$store.commit('liveflight/held_booking', {
                ...this.held_booking,
                expires_at: retrieved.expiresAt,
              });
            }
            const { ancillaries: retrieved_ancillaries = [] } = retrieved || {};
            this.committed_at_retrieve = align_committed_to_catalog(
              retrieved_ancillaries,
              envelope
            );
            // Seed selections from already-booked entries so revisiting
            // /ancillaries doesn't force re-tapping to keep them.
            if (retrieved && retrieved.flight_offer) {
              this.selections =
                retrieved.selections ||
                build_committed_selections(retrieved.flight_offer, envelope);
            }
            // Drop catalog options that ship as fare-bundle freebies (no
            // pricingBreakdown row, flagged by enrich_with_breakdown_price)
            // so a Plus fare's included 20kg bag isn't resold. The bundle
            // premium itself (REGU/SURE/…) keeps its price row and stays
            // as the chosen fare. `steps()` collapses wizard slices that
            // go empty after filtering.
            const included_ancillaries = this.committed_at_retrieve.filter(
              (entry) => entry && entry.included
            );
            this.ancillaries = filter_catalog_by_included(
              envelope,
              included_ancillaries
            );

            // Seatmap diagnostics, previously owned by /seat-selection. A
            // fare that mandates a pick has no forward path when the map
            // can't deliver one, so it exits to /results — also the only
            // destination safe for a forwardsOnly provider. Optional seats
            // just drop the step; `steps()` collapses the nulled slice.
            const seats_mandatory = seats_are_mandatory(this.flight_offer);
            // A rejected fetch leaves no seat step for a fare that can't go
            // on without one (Amadeus lets a seatmap error reject only
            // then). The reason matches /seat-selection's.
            if (fetch_settled.status === 'rejected' && seats_mandatory) {
              this.exit_mandatory_seats('Error from APIs');
              return;
            }
            const { seat: seatmap = null } = this.ancillaries;
            if (seatmap) {
              const reason = diagnose_seatmap(seatmap, seats_mandatory);
              if (reason && seats_mandatory) {
                this.exit_mandatory_seats(reason);
                return;
              }
              if (reason) {
                this.track_seat('seat-map:unavailable', {
                  type: 'info',
                  reason,
                });
                this.ancillaries = { ...this.ancillaries, seat: null };
              }
            } else if (has_capability_for(this.flight_offer, 'shopSeats')) {
              // Providers with no seat offering at all stay silent — an
              // event on every load would be noise, not signal.
              this.track_seat('seat-map:unavailable', {
                type: 'info',
                reason: 'Missing seat map',
              });
            }

            // No steps means no wizard and no advance button — staying here
            // is a blank dead end. Reached when ShopAncillaries rejected but
            // retrieve survived, or every slice filtered out as fare-included.
            if (!this.steps.length) {
              this.$router.replace('checkout');
              return;
            }

            this.active_step_id = this.steps[0].id;
            // Unlock the whole bar only for a real prior user choice —
            // fare-bundle freebies are seeded too and must not count.
            const has_user_selection = this.committed_at_retrieve.some(
              (entry) => entry && !entry.included
            );
            this.visited_step_ids = has_user_selection
              ? this.steps.map((s) => s.id)
              : [this.steps[0].id];
          })
          .finally(() => {
            this.loading = false;
          });
      },
      exit_mandatory_seats(reason) {
        const message =
          'La mappa posti per questo volo non è disponibile su una o più tratte. Ti invitiamo a scegliere un altro volo.';
        this.track_seat('seat-map:mandatory-selection-impossible', {
          type: 'error',
          message,
          reason,
        });
        window.eventBus.$emit('got_modal_notification', {
          msg: message,
          routerPush: 'results',
        });
      },
      // The child can't emit these: the load-time ones fire before the
      // seat step exists.
      track_seat(event, data) {
        this.track_form({
          event,
          data,
          context: seat_tracking_context(
            this.flight_offer,
            this.selections.seat || {}
          ),
        });
      },
      has_seat_step() {
        return this.steps.some((step) => step.id === 'seat');
      },
      has_seat_selections() {
        const seat_selections = this.selections.seat || {};
        return Object.keys(seat_selections).some(
          (segment_id) =>
            Object.keys(seat_selections[segment_id] || {}).length > 0
        );
      },
      /**
       * The commit landed but the repriced offer doesn't carry every seat
       * that was picked. A mandatory fare can't proceed on a partial map, so
       * it warns and re-syncs the step; an optional one carries on.
       */
      handle_seat_price_mismatch() {
        const message =
          'Attenzione! Alcuni posti scelti potrebbero non essere più disponibili.';
        const seats_mandatory = seats_are_mandatory(this.flight_offer);
        this.track_seat('seat-map:submit:fail', {
          type: seats_mandatory ? 'error' : 'warning',
          message,
          reason: 'Mismatch in API Response',
        });
        if (seats_mandatory) {
          window.eventBus.$emit('got_modal_notification', { msg: message });
          this.initialize_ancillaries();
          return;
        }
        window.eventBus.$emit('got_toast_notification', {
          text: message,
          variant: 'warning',
        });
        this.mark_completed(this.active_step_id);
        this.$router.push('checkout');
      },
      set_step_selections(step_id, value) {
        this.selections = { ...this.selections, [step_id]: value };
      },
      set_active_step(step_id) {
        if (!step_id || step_id === this.active_step_id) return;
        if (this.visited_step_ids.indexOf(step_id) < 0) return;
        this.active_step_id = step_id;
      },
      mark_completed(step_id) {
        if (!step_id) return;
        if (this.completed_step_ids.indexOf(step_id) >= 0) return;
        this.completed_step_ids = [...this.completed_step_ids, step_id];
      },
      handle_advance() {
        if (this.commit_loading) return;
        // While the map is in edit mode the click belongs to it: it walks
        // the segments and collapses into its summary on the last one.
        if (this.on_seat_step && this.seat_status.isEditing) {
          window.eventBus.$emit('seat_map_next_step');
          return;
        }
        if (this.is_last_step) {
          this.handle_confirm();
          return;
        }
        const next_step = this.steps[this.active_step_index + 1];
        if (!next_step) return;
        this.mark_completed(this.active_step_id);
        if (this.visited_step_ids.indexOf(next_step.id) < 0) {
          this.visited_step_ids = [...this.visited_step_ids, next_step.id];
        }
        this.active_step_id = next_step.id;
      },
      handle_confirm() {
        const diff = compute_ancillary_diff(
          this.committed_at_retrieve,
          this.selections
        );
        // Only fire seat submit events when the wizard had a seat step, so
        // a bags-only commit doesn't pollute the series.
        const track_seats = this.has_seat_step();
        if (!diff.to_add.length && !diff.to_remove.length) {
          if (track_seats && !this.has_seat_selections()) {
            this.track_seat('seat-map:submit:no-seats', { type: 'info' });
          }
          this.mark_completed(this.active_step_id);
          this.$router.push('checkout');
          return;
        }
        this.commit_loading = true;
        book_ancillaries(
          this.flight_offer,
          this.held_booking.id,
          diff,
          this.ancillaries
        )
          .then(({ flight_offer, seats_priced }) => {
            if (flight_offer) {
              this.$store.commit('liveflight/flight_offer', flight_offer);
            }
            // Clear before either exit — beforeRouteLeave aborts navigation
            // while commit_loading is true, and `.finally` runs AFTER
            // `.then`, so leaving it to finally would trap us here.
            this.commit_loading = false;
            // Providers that can't lose a seat mid-commit leave the flag
            // undefined; only an explicit false is a partial commit.
            if (seats_priced === false) {
              this.handle_seat_price_mismatch();
              return;
            }
            if (track_seats) {
              this.track_seat('seat-map:submit:success', { type: 'info' });
            }
            this.mark_completed(this.active_step_id);
            this.$router.push('checkout');
          })
          .catch((e) => {
            // Rejected diff is stale (BE may have partially applied);
            // re-sync via retrieve + ShopAncillaries before retry to
            // avoid double-booking.
            log('ancillaries.handle_confirm', e);
            if (track_seats) {
              this.track_seat('seat-map:submit:fail', {
                type: 'error',
                message: e.message,
                reason: 'Error from APIs',
              });
            }
            window.eventBus.$emit('got_toast_notification', {
              text: 'Non siamo riusciti a confermare i servizi selezionati.',
              variant: 'danger',
            });
            this.commit_loading = false;
            this.initialize_ancillaries();
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ancillaries-page {
    &__body {
      border-radius: 5px;
    }
    margin-bottom: $grid-gutter-width;
  }

  // Blurs back-btn, body column, and resume wrapper during commit. CSS
  // filter creates a stacking context descendants can't escape, so the
  // advance button is a SIBLING of the resume wrapper — it stays crisp
  // and clickable to surface its own spinner.
  .is-committing {
    filter: blur(3px);
    pointer-events: none;
    user-select: none;
    transition: filter 200ms ease;
  }
</style>
